import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import ModalBase from 'LuxKit/Modal/ModalBase'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import productLabels from 'i18n/productLabels'
import { useForm, useWatch } from 'react-hook-form'
import { removeItems } from 'actions/CheckoutActions'
import determineOrderLoyaltyTransferExperience from 'checkout/selectors/loyalty/determineOrderLoyaltyTransferExperience'
import { getDistanceBetweenPropertyAndAirport } from 'components/Experiences/ExperienceTransfers/ExperienceTransferUtils'
import { generateTransferCheckoutItem } from 'lib/checkout/experiences/cart'
import { getOneClickTransfersItems } from 'checkout/lib/utils/experiences/cart'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getOrderAccommodationStartDate from 'lib/order/getOrderAccommodationStartDate'
import getOrderAccommodationEndDate from 'lib/order/getOrderAccommodationEndDate'
import CheckoutLoyaltyTransferSelectionStep from 'checkout/Components/CheckoutSteps/Review/Loyalty/LoyaltyBenefits/CheckoutLoyaltyTransferSelectionStep'
import CheckoutLoyaltyTransferDetailsStep from 'checkout/Components/CheckoutSteps/Review/Loyalty/LoyaltyBenefits/CheckoutLoyaltyTransferDetailsStep'
import CheckoutLoyaltyHomeToAirportDetailsStep from 'checkout/Components/CheckoutSteps/Review/Loyalty/LoyaltyBenefits/CheckoutLoyaltyHomeToAirportDetailsStep'
import type { HomeToAirportSubmitData } from 'checkout/Components/CheckoutSteps/Review/Loyalty/LoyaltyBenefits/HomeToAirportTransferForm'
import type { LoyaltyTransferFormData } from 'checkout/Components/CheckoutSteps/Review/Loyalty/LoyaltyBenefits/checkoutLoyaltyTransferTypes'
import LineHomeIcon from 'LuxKit/Icons/line/LineHomeIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineArrowsSwapHorizontalIcon from 'LuxKit/Icons/line/LineArrowsSwapHorizontalIcon'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import LineBuildingIcon from 'LuxKit/Icons/line/LineBuildingIcon'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import determineLoyaltyEligibleTransferOptions from 'checkout/selectors/loyalty/determineLoyaltyEligibleTransferOptions'
import { hasConfiguredHomeToAirportOption } from 'loyalty/lib/transferOfferUtils'

type ModalStep = 'selection' | 'details'
type TransferType = App.LoyaltyTransferType

interface Props {
  order: App.Order
  onAddItems: (items: Array<App.Checkout.TransferItem>) => void
  /**
   * The default step this modal will start on
   * @default selection
   */
  defaultStep?: ModalStep
  /**
   * Pass the current transfer view to set default values from existing data
   */
  transferView?: App.Checkout.UnifiedTransferView
  afterRemoveItems?: () => void
}

function LoyaltyOrderViewTransferModal({ order, onAddItems, defaultStep, transferView, afterRemoveItems }: Props) {
  const { resolve } = useModalElementContext()
  const intl = useIntl()
  const loyaltyConfig = useAppSelector(state => state.loyalty.config)
  const hasHomeToAirportOption = hasConfiguredHomeToAirportOption(loyaltyConfig)
  const dispatch = useAppDispatch()
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const experienceLists = useAppSelector(state => state.experience.experienceLists)
  const experiences = useAppSelector(state => state.experience.experiences)
  const experienceTransferOptions = useAppSelector(state => state.experience.experienceTransferOptions)
  const transferExperience = useMemo(
    () => determineOrderLoyaltyTransferExperience(order, experienceLists, experiences),
    [order, experienceLists, experiences],
  )
  const loyaltyEligibleOptions = useMemo(
    () => determineLoyaltyEligibleTransferOptions(order, transferExperience, experienceTransferOptions),
    [order, transferExperience, experienceTransferOptions],
  )

  const hasUserSelectedTransferType = useRef(false)
  const isEditing = !!transferView?.hasTransfers

  const property = order.items[0]?.offer?.property
  const startDate = getOrderAccommodationStartDate(order)
  const endDate = getOrderAccommodationEndDate(order)

  const totalOccupants = useMemo(() => {
    const item = order.items[0]
    if (!item?.reservation) return { adults: 2, children: 0 }
    return {
      adults: item.reservation.adults || 2,
      children: item.reservation.children || 0,
    }
  }, [order])

  const defaultTransfers = useMemo((): { toHotel: App.ExperienceTransferView; toAirport: App.ExperienceTransferView } => {
    const airport = transferExperience?.originalExperience?.airport
    return {
      toHotel: {
        type: 'AIRPORT-TO-HOTEL',
        date: startDate,
        travellers: {
          adults: totalOccupants.adults,
          children: totalOccupants.children,
        },
        hotel: {
          latitude: property?.latitude,
          longitude: property?.longitude,
          kmFromAirport: getDistanceBetweenPropertyAndAirport(property as any, airport),
          name: property?.name,
        },
        baggage: { bags: 0, oversizedBags: 0 },
      },
      toAirport: {
        type: 'HOTEL-TO-AIRPORT',
        date: endDate,
        travellers: {
          adults: totalOccupants.adults,
          children: totalOccupants.children,
        },
        hotel: {
          latitude: property?.latitude,
          longitude: property?.longitude,
          kmFromAirport: getDistanceBetweenPropertyAndAirport(property as any, airport),
          name: property?.name,
        },
        baggage: { bags: 0, oversizedBags: 0 },
      },
    }
  }, [startDate, endDate, totalOccupants, property, transferExperience])

  const hotelFallbackLabel = intl.formatMessage(productLabels.hotel)
  const airportFallbackLabel = intl.formatMessage({
    id: '3hlH1n8GJevqeMXzqGJT',
    defaultMessage: 'Airport',
    description: 'Fallback airport label in the loyalty transfer booking modal when the airport name is missing',
  })
  const hotelName = defaultTransfers.toHotel.hotel?.name || hotelFallbackLabel
  const airportName = transferExperience?.originalExperience?.airport?.name || airportFallbackLabel

  const getDefaultTransferType = useCallback((): TransferType => {
    if (transferView?.transferType) return transferView.transferType
    if (loyaltyEligibleOptions.canUseReturn) return 'FREE_RETURN_TRANSFER'
    if (loyaltyEligibleOptions.canUseOneWay) return 'ONE_WAY_ARRIVAL'
    return 'HOME_TO_AIRPORT'
  }, [transferView?.transferType, loyaltyEligibleOptions])

  const [currentStep, setCurrentStep] = useState<ModalStep>(defaultStep ?? 'selection')

  const form = useForm<LoyaltyTransferFormData>({
    defaultValues: {
      transferType: getDefaultTransferType(),
      pickUpPlaceId: '',
      dropOffAirportCode: '',
      specialRequests: '',
      selectedTransferOption: undefined,
    },
  })

  const watchedTransferType = useWatch({ control: form.control, name: 'transferType' })

  useEffect(() => {
    if (hasUserSelectedTransferType.current) return
    let defaultTransferType: TransferType = 'HOME_TO_AIRPORT'
    if (transferView?.transferType) {
      defaultTransferType = transferView.transferType
    } else if (loyaltyEligibleOptions.canUseReturn) {
      defaultTransferType = 'FREE_RETURN_TRANSFER'
    } else if (loyaltyEligibleOptions.canUseOneWay) {
      defaultTransferType = 'ONE_WAY_ARRIVAL'
    } else if (!hasHomeToAirportOption) {
      return
    }
    form.reset({
      transferType: defaultTransferType,
      pickUpPlaceId: transferView?.pickUpPlaceId ?? '',
      dropOffAirportCode: transferView?.dropOffAirportCode ?? '',
      specialRequests: '',
      selectedTransferOption: undefined,
    })
  }, [loyaltyEligibleOptions, form, hasHomeToAirportOption, transferView])

  const paidReturnTransferPricing = useMemo(() => {
    if (!loyaltyEligibleOptions.bestToHotelOption || !loyaltyEligibleOptions.bestToAirportOption) return undefined
    let freeTransferOption: App.ExperienceTransferOption | undefined
    if (loyaltyEligibleOptions.bestToHotelOption?.loyaltyOptions?.eligibleTransfer === 'ONE-WAY') {
      freeTransferOption = loyaltyEligibleOptions.bestToHotelOption
    } else if (loyaltyEligibleOptions.bestToAirportOption?.loyaltyOptions?.eligibleTransfer === 'ONE-WAY') {
      freeTransferOption = loyaltyEligibleOptions.bestToAirportOption
    }
    if (!freeTransferOption) return undefined

    const paidTransferOption = loyaltyEligibleOptions.bestToAirportOption
    return {
      totalPrice: paidTransferOption.price,
      freePrice: 0,
      paidPrice: paidTransferOption.price,
      freeTransferOption,
      paidTransferOption,
      freeDirection: freeTransferOption === loyaltyEligibleOptions.bestToHotelOption ? 'AIRPORT-TO-HOTEL' : 'HOTEL-TO-AIRPORT',
      paidDirection: 'HOTEL-TO-AIRPORT',
    }
  }, [loyaltyEligibleOptions])

  const transferOptions = useMemo(() => {
    const options = []
    if (loyaltyEligibleOptions.canUseReturn && loyaltyEligibleOptions.bestToHotelOption && loyaltyEligibleOptions.bestToAirportOption) {
      options.push({
        id: 'FREE_RETURN_TRANSFER',
        title: intl.formatMessage({ id: 'My-9LjnciAYb8cVT6IBq', defaultMessage: 'Free round-trip transfers', description: 'Title for the free round-trip transfer details selection step' }),
        subtitle: <BodyText variant="large" colour="light-foreground-alt">{intl.formatMessage({ id: 'wxUF7VzTj8aU9PxlFHnH', defaultMessage: 'Airport', description: 'Airport location label in loyalty transfer option subtitle' })} <LineArrowsSwapHorizontalIcon /> {intl.formatMessage(productLabels.hotel)}</BodyText>,
        icon: <LinePlaneDepartureIcon />,
        available: true,
        price: null,
        transferOptions: { toHotel: loyaltyEligibleOptions.bestToHotelOption, toAirport: loyaltyEligibleOptions.bestToAirportOption },
      })
    } else if (loyaltyEligibleOptions.canUseOneWay) {
      if (loyaltyEligibleOptions.bestToHotelOption) {
        options.push({
          id: 'ONE_WAY_ARRIVAL',
          title: intl.formatMessage({ id: 'yc6eTe459lfWKH8W-tC-', defaultMessage: 'Free one-way transfer', description: 'Title for a complimentary one-way loyalty transfer option' }),
          subtitle: <BodyText variant="large" colour="light-foreground-alt">{intl.formatMessage({ id: 'wxUF7VzTj8aU9PxlFHnH', defaultMessage: 'Airport', description: 'Airport location label in loyalty transfer option subtitle' })} <LineArrowRightIcon /> {intl.formatMessage(productLabels.hotel)}</BodyText>,
          icon: <LinePlaneDepartureIcon />,
          available: true,
          price: null,
          transferOptions: { toHotel: loyaltyEligibleOptions.bestToHotelOption },
        })
      }
      if (loyaltyEligibleOptions.bestToAirportOption) {
        options.push({
          id: 'ONE_WAY_DEPARTURE',
          title: intl.formatMessage({ id: 'yc6eTe459lfWKH8W-tC-', defaultMessage: 'Free one-way transfer', description: 'Title for a complimentary one-way loyalty transfer option' }),
          subtitle: <BodyText variant="large" colour="light-foreground-alt">{intl.formatMessage(productLabels.hotel)} <LineArrowRightIcon /> {intl.formatMessage({ id: 'wxUF7VzTj8aU9PxlFHnH', defaultMessage: 'Airport', description: 'Airport location label in loyalty transfer option subtitle' })}</BodyText>,
          icon: <LineBuildingIcon />,
          available: true,
          price: null,
          transferOptions: { toAirport: loyaltyEligibleOptions.bestToAirportOption },
        })
      }
      if (loyaltyEligibleOptions.bestToHotelOption && loyaltyEligibleOptions.bestToAirportOption && paidReturnTransferPricing) {
        options.push({
          id: 'PAID_RETURN_TRANSFER',
          title: intl.formatMessage({ id: 'FmckT9a0dmaibYt6rekP', defaultMessage: 'Round-trip transfers', description: 'Title for a paid round-trip loyalty transfer upgrade option' }),
          subtitle: <BodyText variant="large" colour="light-foreground-alt">{intl.formatMessage({ id: 'wxUF7VzTj8aU9PxlFHnH', defaultMessage: 'Airport', description: 'Airport location label in loyalty transfer option subtitle' })} <LineArrowsSwapHorizontalIcon /> {intl.formatMessage(productLabels.hotel)}</BodyText>,
          icon: <LinePlaneDepartureIcon />,
          available: true,
          price: paidReturnTransferPricing.paidPrice,
          upgradeLabel: intl.formatMessage({ id: 'XlJYGBtNiVarS4s8T99H', defaultMessage: 'Upgrade to return', description: 'Label for upgrading a one-way to a return loyalty transfer' }),
          isPopular: true,
        })
      }
    }
    if (hasHomeToAirportOption) {
      options.push({
        id: 'HOME_TO_AIRPORT',
        title: intl.formatMessage({ id: 'yc6eTe459lfWKH8W-tC-', defaultMessage: 'Free one-way transfer', description: 'Title for a complimentary one-way loyalty transfer option' }),
        subtitle: <BodyText variant="large" colour="light-foreground-alt">{intl.formatMessage({ id: '6OWHjfsi3ULKqCg027NQ', defaultMessage: 'Home', description: 'Home location label in loyalty transfer option subtitle' })} <LineArrowRightIcon /> {intl.formatMessage({ id: 'wxUF7VzTj8aU9PxlFHnH', defaultMessage: 'Airport', description: 'Airport location label in loyalty transfer option subtitle' })}</BodyText>,
        icon: <LineHomeIcon />,
        available: true,
        price: null,
      })
    }
    return options
  }, [loyaltyEligibleOptions, paidReturnTransferPricing, hasHomeToAirportOption, intl])

  const selectedTransferOption = useMemo(() => {
    if (watchedTransferType === 'ONE_WAY_ARRIVAL') return loyaltyEligibleOptions.bestToHotelOption
    if (watchedTransferType === 'ONE_WAY_DEPARTURE') return loyaltyEligibleOptions.bestToAirportOption
    if (watchedTransferType === 'FREE_RETURN_TRANSFER' || watchedTransferType === 'PAID_RETURN_TRANSFER') {
      return loyaltyEligibleOptions.bestToAirportOption || loyaltyEligibleOptions.bestToHotelOption
    }
    return undefined
  }, [watchedTransferType, loyaltyEligibleOptions])

  const handleContinueToDetails = useCallback(() => {
    hasUserSelectedTransferType.current = true
    setCurrentStep('details')
  }, [])

  const handleBackToSelection = useCallback(() => {
    setCurrentStep('selection')
  }, [])

  const getDetailsStepTitle = useCallback(() => {
    switch (watchedTransferType) {
      case 'HOME_TO_AIRPORT':
      case 'ONE_WAY_ARRIVAL':
      case 'ONE_WAY_DEPARTURE':
        return intl.formatMessage({ id: 'yc6eTe459lfWKH8W-tC-', defaultMessage: 'Free one-way transfer', description: 'Title for a complimentary one-way loyalty transfer option' })
      case 'FREE_RETURN_TRANSFER':
        return intl.formatMessage({ id: '2M99Hx4wushiLFIMDxyC', defaultMessage: 'Free round-trip transfers', description: 'Title for the free round-trip transfers details step' })
      case 'PAID_RETURN_TRANSFER':
        return intl.formatMessage({ id: 'zaNKonIxWKU_aPiPKWpv', defaultMessage: 'Round-trip transfers', description: 'Title for the paid round-trip transfers details step' })
      default:
        return intl.formatMessage({ id: 'yTLIiBw7IuY17UpvTQ4t', defaultMessage: 'Transfer details', description: 'Default fallback title for the transfer details step' })
    }
  }, [watchedTransferType, intl])

  const detailsStepSubtitle = useMemo(() => {
    switch (watchedTransferType) {
      case 'HOME_TO_AIRPORT':
        return intl.formatMessage({ id: 'LwjGscikHwTRwYDSHlKa', defaultMessage: 'Home to Airport', description: 'Subtitle for the home-to-airport loyalty transfer option' })
      case 'FREE_RETURN_TRANSFER':
      case 'PAID_RETURN_TRANSFER':
        return intl.formatMessage({ id: 'jLdmYPVsT7oRRsXtCfAQ', defaultMessage: '{airportName} to {hotelName}, return', description: 'Subtitle for the round-trip loyalty transfer with route names' }, { airportName, hotelName })
      case 'ONE_WAY_ARRIVAL':
        return intl.formatMessage({ id: 'NQespEBTDD3jRd_cpz_7', defaultMessage: '{airportName} to {hotelName}', description: 'Subtitle for the one-way arrival loyalty transfer with route names' }, { airportName, hotelName })
      case 'ONE_WAY_DEPARTURE':
        return intl.formatMessage({ id: 'ZTXcI43SF9WvOy1Im0Ik', defaultMessage: '{hotelName} to {airportName}', description: 'Subtitle for the one-way departure loyalty transfer with route names' }, { hotelName, airportName })
      default:
        return intl.formatMessage({ id: 'O9jnordZH-niiLvvhJxc', defaultMessage: 'Private Transfers', description: 'Default fallback subtitle for the loyalty transfer details step' })
    }
  }, [watchedTransferType, hotelName, airportName, intl])

  const handleSubmit = form.handleSubmit((data) => {
    const { transferType, specialRequests } = data
    if (!transferExperience || (!loyaltyEligibleOptions.bestToHotelOption && !loyaltyEligibleOptions.bestToAirportOption)) return

    const travellers = defaultTransfers.toHotel.travellers
    const bestOptions = {
      bestToHotelOption: loyaltyEligibleOptions.bestToHotelOption,
      bestToAirportOption: loyaltyEligibleOptions.bestToAirportOption,
    }
    const { toHotel, toAirport } = getOneClickTransfersItems(
      transferExperience,
      travellers,
      defaultTransfers,
      bestOptions,
      specialRequests,
    )

    const items: Array<App.Checkout.TransferItem> = []

    if (transferType === 'FREE_RETURN_TRANSFER' && toHotel && toAirport) {
      items.push(
        { ...generateTransferCheckoutItem(transferExperience.originalExperience, toHotel), hasLoyaltyBenefit: true, isLoyaltyTransfer: true },
        { ...generateTransferCheckoutItem(transferExperience.originalExperience, toAirport), hasLoyaltyBenefit: true, isLoyaltyTransfer: true },
      )
    } else if (transferType === 'ONE_WAY_ARRIVAL' && toHotel) {
      items.push({ ...generateTransferCheckoutItem(transferExperience.originalExperience, toHotel), hasLoyaltyBenefit: true, isLoyaltyTransfer: true })
    } else if (transferType === 'ONE_WAY_DEPARTURE' && toAirport) {
      items.push({ ...generateTransferCheckoutItem(transferExperience.originalExperience, toAirport), hasLoyaltyBenefit: true, isLoyaltyTransfer: true })
    } else if (transferType === 'PAID_RETURN_TRANSFER' && toHotel && toAirport) {
      const freeTransferOption = loyaltyEligibleOptions.bestToHotelOption?.loyaltyOptions?.eligibleTransfer === 'ONE-WAY'
        ? loyaltyEligibleOptions.bestToHotelOption
        : loyaltyEligibleOptions.bestToAirportOption
      const hotelIsFree = freeTransferOption === loyaltyEligibleOptions.bestToHotelOption
      items.push(
        { ...generateTransferCheckoutItem(transferExperience.originalExperience, toHotel), hasLoyaltyBenefit: hotelIsFree, isLoyaltyTransfer: true },
        { ...generateTransferCheckoutItem(transferExperience.originalExperience, toAirport), hasLoyaltyBenefit: !hotelIsFree, isLoyaltyTransfer: true },
      )
    }

    if (items.length > 0) {
      if (transferView?.hasTransfers) {
        const existingIds = [
          ...transferView.loyaltyTransfers.map(item => item.itemId),
          ...transferView.transferItems.map(item => item.itemId),
        ]
        if (existingIds.length) {
          dispatch(removeItems(existingIds))
        }
      }
      onAddItems(items)
      const snackbarMessage = transferType === 'ONE_WAY_ARRIVAL'
        ? intl.formatMessage({ id: 'RC7SNllcXGdJN4QJ7I0Z', defaultMessage: 'One-way transfer (Airport to hotel) added', description: 'Snackbar message after adding airport-to-hotel transfer' })
        : transferType === 'ONE_WAY_DEPARTURE'
          ? intl.formatMessage({ id: 'pXg8r9kx4VHuX785Z2sl', defaultMessage: 'One-way transfer (Hotel to airport) added', description: 'Snackbar message after adding hotel-to-airport transfer' })
          : intl.formatMessage({ id: 'o3r75uMvSn8QpHv4d4W6', defaultMessage: 'Round-trip transfers (Airport to Hotel) added', description: 'Snackbar message after adding round-trip transfers' })
      showSnackbar(snackbarMessage, 'success', { icon: <LineCheckCircleIcon /> })
    }
    resolve()
  })

  const handleHomeToAirportSubmit = useCallback((data: HomeToAirportSubmitData) => {
    const { transfer, experience } = data

    const transferItem: App.Checkout.TransferItem = {
      ...generateTransferCheckoutItem(experience, transfer),
      hasLoyaltyBenefit: true,
      isLoyaltyTransfer: true,
    }
    if (transferView?.hasTransfers) {
      const existingIds = [
        ...transferView.loyaltyTransfers.map(item => item.itemId),
        ...transferView.transferItems.map(item => item.itemId),
      ]
      if (existingIds.length) {
        dispatch(removeItems(existingIds))
      }
    }
    onAddItems([transferItem])
    showSnackbar(intl.formatMessage({ id: 'QiNbjODDBcIM-YXdtTcT', defaultMessage: 'One-way transfer (Home to airport) added', description: 'Success snackbar after adding a home-to-airport loyalty transfer' }), 'success', { icon: <LineCheckCircleIcon /> })
    resolve()
  }, [onAddItems, resolve, intl, transferView, dispatch])

  const handleRemove = useCallback(() => {
    if (!transferView) return

    const itemIds = [
      ...transferView.loyaltyTransfers.map(item => item.itemId),
      ...transferView.transferItems.map(item => item.itemId),
    ]
    if (itemIds.length > 0) {
      dispatch(removeItems(itemIds))
    }

    afterRemoveItems?.()
    resolve()
  }, [transferView, dispatch, resolve, afterRemoveItems])

  if (!transferOptions.length) return null

  const currentTransferView = isEditing
    ? transferView
    : {
        hasTransfers: false,
        isEditing: false,
        loyaltyTransfers: [] as Array<App.Checkout.LoyaltyTransferItem>,
        transferItems: [] as Array<App.Checkout.TransferItem>,
        transferType: undefined,
        pickUpPlaceId: undefined,
        dropOffAirportCode: undefined,
      }

  return (
    <ModalBase size="L" height="max" tabletHeight="auto">
      {currentStep === 'selection' && (
        <CheckoutLoyaltyTransferSelectionStep
          form={form}
          transferOptions={transferOptions}
          onContinue={handleContinueToDetails}
        />
      )}
      {currentStep === 'details' && watchedTransferType !== 'HOME_TO_AIRPORT' && transferExperience && (
        <CheckoutLoyaltyTransferDetailsStep
          form={form}
          title={getDetailsStepTitle()}
          subtitle={detailsStepSubtitle}
          selectedTransferOption={selectedTransferOption}
          oneClickTransfer={transferExperience}
          tier={tier}
          paidReturnTransferPricing={paidReturnTransferPricing}
          currentTransferView={currentTransferView}
          onBack={handleBackToSelection}
          onSubmit={handleSubmit}
          onRemove={isEditing ? handleRemove : undefined}
        />
      )}
      {currentStep === 'details' && watchedTransferType === 'HOME_TO_AIRPORT' && (
        <CheckoutLoyaltyHomeToAirportDetailsStep
          title={getDetailsStepTitle()}
          subtitle={detailsStepSubtitle}
          currentTransferView={currentTransferView}
          onBack={handleBackToSelection}
          onSubmit={handleHomeToAirportSubmit}
          onRemove={isEditing ? handleRemove : undefined}
        />
      )}
    </ModalBase>
  )
}

export default LoyaltyOrderViewTransferModal
